import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { buttonVariants } from "utils/muiTailwindStyles";

type UpdatePasswordFormType = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

interface Props {
  isSubmitting: boolean;
  isValid: boolean;
  isSuccess: boolean;
}

const passwordRules = [
  {
    id: "len",
    test: (v: string) => v.length >= 12 && v.length <= 20,
    label: "12-20 characters",
  },
  {
    id: "upper",
    test: (v: string) => /[A-Z]/.test(v),
    label: "At least one uppercase letter",
  },
  {
    id: "lower",
    test: (v: string) => /[a-z]/.test(v),
    label: "At least one lowercase letter",
  },
  {
    id: "digit",
    test: (v: string) => /\d/.test(v),
    label: "At least one number",
  },
  {
    id: "symbol",
    test: (v: string) => /[^A-Za-z0-9]/.test(v),
    label: "At least one symbol",
  },
];

function UpdatePasswordForm({ isSubmitting, isValid, isSuccess }: Props) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<UpdatePasswordFormType>();
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const newPassword = watch("newPassword");
  const confirmNewPassword = watch("confirmNewPassword");

  return (
    <>
      <Box className="text-center space-y-6">
        <Typography
          variant="h5"
          component="h5"
          className="text-f22 leading-7 text-surface-dark"
        >
          Reset Password
        </Typography>
        <Typography variant="body2" className="text-black text-base px-4">
          Enter your current password and choose a new one.
        </Typography>
      </Box>

      <Box className="space-y-6">
        <Controller
          name="currentPassword"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="currentPassword"
              label="Current Password"
              variant="outlined"
              type={showCurrent ? "text" : "password"}
              className="w-full"
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle current password visibility"
                        onClick={() => setShowCurrent((v) => !v)}
                        edge="end"
                      >
                        {showCurrent ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: 20, pattern: String.raw`[\x20-\x7E]*` },
              }}
            />
          )}
        />

        <Controller
          name="newPassword"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="newPassword"
              label="New Password"
              variant="outlined"
              type={showNew ? "text" : "password"}
              className="w-full"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle new password visibility"
                        onClick={() => setShowNew((v) => !v)}
                        edge="end"
                      >
                        {showNew ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: 20, pattern: String.raw`[\x20-\x7E]*` },
              }}
            />
          )}
        />

        <Box className="bg-color_F8F8F8 rounded-lg p-4">
          <Typography className="text-sm mb-2">
            Password must meet all rules:
          </Typography>
          <ul className="list-disc pl-5 space-y-1">
            {passwordRules.map((rule) => {
              const passed = rule.test(newPassword || "");
              return (
                <li
                  key={rule.id}
                  className={passed ? "text-green-600" : "text-gray-600"}
                >
                  {rule.label}
                </li>
              );
            })}
          </ul>
        </Box>

        <Controller
          name="confirmNewPassword"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="confirmNewPassword"
              label="Confirm New Password"
              variant="outlined"
              type={showConfirm ? "text" : "password"}
              className="w-full"
              error={!!errors.confirmNewPassword}
              helperText={errors.confirmNewPassword?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirm((v) => !v)}
                        edge="end"
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: 20, pattern: String.raw`[\x20-\x7E]*` },
              }}
            />
          )}
        />

        <Typography
          className={
            newPassword &&
            confirmNewPassword &&
            newPassword === confirmNewPassword
              ? "text-green-600 text-sm"
              : "text-gray-600 text-sm"
          }
        >
          The passwords must match
        </Typography>

        <Button
          type="submit"
          variant="contained"
          className={`${buttonVariants.primary.className} w-full text-base rounded-full py-3 disabled:text-surface-light disabled:opacity-40`}
          sx={{ ...buttonVariants.primary.sx }}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : isSuccess ? (
            "Updated"
          ) : (
            "Update"
          )}
        </Button>
      </Box>
    </>
  );
}

export default UpdatePasswordForm;
